export type Severity = 'critical' | 'warning' | 'info';
export type ItemStatus = 'open' | 'claimed' | 'done' | 'dismissed';
export type FunnelStep = 'task' | 'traffic' | 'session' | 'add_to_cart' | 'checkout';

export interface ActionItem {
  item_id: string;
  source: string;
  store: string | null;
  severity: Severity;
  funnel_step: FunnelStep;
  unit_type: string | null;
  unit_label: string | null;
  unit_parent: string | null;
  // `stores` is set on a Watchdog card shared by several stores (a Clarity sync, one Google Ads
  // account); such a card has no `store` and belongs in each listed store's view.
  unit_keys?: { stores?: string[] } | null;
  deep_link: string | null;
  headline: string | null;
  magnitude: number | null;
  campaign_id?: string | null;
  campaign_name?: string | null;
  target_roas?: number | null;
  breakeven_roas?: number | null;
  status: ItemStatus;
  claimed_by: string | null;
  claimed_at: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  resolution_kind: string | null;
  linked_change_key: string | null;
  dismiss_reason: string | null;
  surface_on: string;
  due_on: string | null;
  review_criteria: string | null;
  first_seen: string;
  last_seen: string;
  episode_count?: number | null;
  episode_first_opened?: string | null;
  episode_latest_opened?: string | null;
  card: CardPayload | null;
}

// Mirror of sbly-dash-collector src/intelligence/googleWatchdog/caseRecord.ts.
export interface CaseResolution {
  resolvedBy: string | null;
  resolvedAt: string | null;
  note: string | null;
  kind: string | null;
  dismissReason: string | null;
}

export interface CaseHistoryEntry {
  changedAt: string;
  actor: string | null;
  description: string | null;
  rationale: string | null;
  changeCategory: string | null;
}

export interface CaseEpisode {
  itemId: string;
  firstSeen: string;
  lastSeen: string;
  status: string;
  severity: string;
  magnitude: number | null;
  headline: string | null;
  resolution: CaseResolution;
  history: CaseHistoryEntry[];
}

export interface CaseRecord {
  naturalKey: string | null;
  episodeCount: number;
  episodes: CaseEpisode[];
}

export interface ChangeRow {
  change_key: string;
  source: string;
  store: string | null;
  changed_at: string;
  actor: string | null;
  unit_type: string | null;
  unit_label: string | null;
  campaign_id: string | null;
  url: string | null;
  summary: string;
  old_value: string | null;
  new_value: string | null;
  rationale: string | null;
  change_category: string | null;
  linked_item_id: string | null;
  ingested_at: string;
  burst_id?: string | null;
  burst_summary?: string | null;
}

// One row per human action from GET /changes (grouped default): bursts collapse to a
// single row keyed by burst_id; solo/manual rows pass through with event_count 1.
// old/new only survive for single-event groups. Mirror of the API server's
// buildListChangesGroupedSql: mirror, do not invent.
export interface ChangeGroup {
  change_key: string;
  changed_at: string;
  store: string | null;
  actor: string | null;
  source: string;
  summary: string;
  old_value: string | null;
  new_value: string | null;
  event_count: number;
  campaign_id: string | null;
  unit_label: string | null;
  url: string | null;
  rationale: string | null;
  linked_item_id: string | null;
  change_category: string | null;
}

export interface StoreSummary { store: string; new_today: number; claimed: number; overdue: number }

export const STORES = ['US', 'CA', 'AU', 'UK', 'ARALUNA', 'NOONLASH'] as const;
export const FUNNEL_STEP_LABELS: Record<FunnelStep, string> = {
  task: 'Task', traffic: 'Traffic', session: 'Session', add_to_cart: 'Add to cart', checkout: 'Checkout',
};
export const SEVERITY_COLORS: Record<Severity, string> = {
  critical: '#DC2626', warning: '#D97706', info: '#64748B',
};

export interface CardFindingLine { severity: 'critical' | 'warning'; text: string; isNew: boolean }

export interface CardBrief {
  basis: string;
  cause: string;
  explanation: string;
  evidenceSummary: string;
  recommended: string | null;
  diagnoses: { rank: number; cause: string; action: string; basis: string; caveat?: string }[];
  contributing: string[];
  beyondTheData: { hypothesis: string; why: string }[];
  synthesizedAt: string;
}

// Mirror of sbly-dash-collector src/intelligence/googleWatchdog/issues.ts.
// Mirror of sbly-dash-collector src/intelligence/googleWatchdog/heroMetric.ts. Already
// formatted by the backend, which is the only side that knows each finding's unit: 0.116 is
// "11.6%" for a funnel rate and "$0.12" for a cost.
export interface HeroMetric {
  label: string;
  value: string;
  /** e.g. "3.0x goal" — carries its own comparison word, or absent when there is none. */
  baseline?: string;
  /** The same comparison split for a table: "3.0x" in the cell, "goal" in the header. */
  baselineValue?: string;
  baselineLabel?: string;
  /** e.g. "up 9%", "down 0.4 points". Already in the unit's own terms. */
  change?: string;
  dir?: 'up' | 'down';
  /** e.g. "Last 7 days" — what stretch `value` covers. Absent for a single-day measurement. */
  window?: string;
  tone: 'bad' | 'neutral' | 'good';
}

export interface IssueTableRow {
  subject: string;
  link: string | null;
  /** One per column, in `columns` order. Null where this row did not measure it. */
  cells: (string | null)[];
}

/** An issue's findings as rows, when they are about named things rather than the campaign. */
export interface IssueTable {
  subjectLabel: string;
  columns: string[];
  rows: IssueTableRow[];
}

export interface CardIssue {
  key: string;
  title: string;
  where: string;
  whatWeSee: string;
  whatToDo: string;
  dollars: number | null;
  /** Prefix for the money on this object; absent on cards stored before 2026-09-09, fall back to the store's. */
  currency?: string;
  severity: 'critical' | 'warning';
  /** The number that tripped. Optional: payloads predating the field carry none. */
  metric?: HeroMetric | null;
  /**
   * The issue's findings as rows, when they are about named entities. Takes the place of both
   * the single-metric row and the prose the numbers came from. Null or absent when the issue is
   * about the campaign itself, or on payloads predating the field.
   */
  table?: IssueTable | null;
  /**
   * Which of the five return levers this issue explains, so the lever table can print the
   * finding under the lever that moved instead of leaving the reader to pair them by eye.
   * Null when the issue's mechanism lands on no single lever; absent on older payloads.
   */
  explainsLever?: LeverKey | null;
  /**
   * The checks that raised this issue, lead check first, in the words the deck uses for
   * them. This is what lets each issue carry its own "here is who looked, and what they
   * refuse to claim" instead of pointing at one shared receipt at the foot of the card.
   * Absent on payloads predating the field.
   */
  foundBy?: WatchdogCheckDefinition[];
  detections: CardFindingLine[];
  brief?: CardBrief;
}

// Mirror of sbly-dash-collector src/intelligence/googleWatchdog/economics.ts.
export type EconState = 'losing_money' | 'profitable_under_goal' | 'under_goal' | 'on_target' | 'no_target' | 'unknown';

// Mirror of sbly-dash-collector src/intelligence/googleWatchdog/economics.ts.
export interface EconomicsFrame {
  state: EconState;
  statePhrase: string;            // queue-row words
  bottomLine: string;             // the ladder sentence for the card top
  breakevenOnFile: boolean;
  breakevenRoas: number | null;
  targetRoas: number | null;
  roas7d: number | null;          // last 7 days, prose only
  roas30?: number | null;         // last 30 days: the window the verdict and profit are judged on
  monthlyProfitUsd: number | null; // last 30 days, store currency; negative = losing
  monthlyGapUsd: number | null;
  magnitudeBasis: 'loss' | 'impact'; // what item.magnitude measures on this card
}

// Computed-economics card fields. Mirror of sbly-dash-collector
// src/intelligence/googleWatchdog/feed.ts (CardRoasStack / CardBand / CardDivergence /
// CardSeries). The collector's assembleFeed attaches these to every campaign card when
// population data loads; they are absent on pre-Merge-1 payloads, non-campaign digests,
// and degraded nights. Mirror, don't invent.
export interface CardRoasStack {
  ncRoas30: number | null;        // new-customer ROAS, 30d
  ncRoas7: number | null;         // new-customer ROAS, 7d
  breakeven: number | null;       // computed per-family break-even
  family: string | null;          // the product family the break-even came from
  googleTarget: number | null;    // the campaign's Google Ads target ROAS with the store's tax share taken out
  twAchieved7: number | null;     // Triple Whale measured ROAS, 7d
  googleAchieved7: number | null; // Google-reported ROAS, 7d
  divergenceFlag: boolean;
}
export interface CardBand {
  median: number;
  scaledMad: number;
  low: boolean;                   // thin data: band collapsed, no reliable position
  position: string | null;
  // median/scaledMad measure the trailing-week ROAS series, so the stripe the chart paints is
  // on the same footing as the line inside it. judgedValue and position are the collector's own
  // read of where the campaign sits; nothing in the dashboard draws them.
  judgedValue: number | null;
  judgedDay?: string;             // the day the judged week ends on; absent on payloads predating it
  // The ROAS alert's own baseline: the median of its eight baseline weeks, and the robust CV of
  // those same weeks. usual x (1 +/- wobble) is the range the chart shades, so the shaded area IS
  // the rule the campaign is judged against rather than a second opinion about it. All three are
  // null together when the campaign has too little history for that alert to judge it, and absent
  // together on payloads predating the field.
  usual?: number | null;
  wobble?: number | null;
  baselineWeeks?: number | null;
}
export interface CardDivergence {
  pct: number | null;             // how far Google's ROAS sits above Triple Whale's
  firing: boolean;
  streakDays: number;
}
export interface CardRoasSeriesPoint {
  date: string;
  twRoasDaily: number | null;
  twRoasRolling7: number | null;
  googleRoasDaily: number | null;
  spend: number;
}
export interface CampaignFunnelDailyRow {
  date: string;
  clicks: number;
  sessions: number;
  atc: number;
  orders: number;
}
export interface CardSeries {
  roas: CardRoasSeriesPoint[];
  funnelDaily: CampaignFunnelDailyRow[];
}

/** First day of a new level in a daily series, found by code (never the AI). */
export interface DropStart {
  mostLikely: string;
  from: string;
  to: string;
  gain: number;
  /** First day `before` was measured from: the previous change, when the series moved twice. */
  beforeStart: string;
  before: number;
  after: number;
}

/** Money lost per day if nobody acts. Used only to order the list. */
export interface CardCostOfDelay {
  perDayUsd: number;
  basis: string;
  dropStart: DropStart | null;
}

export type CardProblemFigure =
  | { kind: 'change'; before: string; after: string; dir: 'up' | 'down'; tone: 'bad' | 'good' }
  | { kind: 'text'; text: string };

export interface CardProblem {
  headline: string;
  subject: string;
  figure: CardProblemFigure | null;
}

export interface CardRecovery {
  figure: CardProblemFigure;
}

// Row-face metric strip and digest children mirror the collector's CardPayload
// contract (sbly-dash-collector src/intelligence/googleWatchdog/feed.ts): mirror,
// don't invent. bundleType now includes 'campaign_digest' | 'lp_digest'.
export interface CardFaceStat { label: string; value: string; dir?: 'up' | 'down' }

// Row metric grammar. v1.1 cards additionally carry the slot-grammar fields;
// pre-v1.1 cards carry only primary/support. Mirror of collector feed.ts
// CardFace; mirror, don't invent.
export interface CardFaceDelta {
  value: string;                 // e.g. "ROAS 1.9x"
  reference?: string;            // e.g. "target 2.5x" or "was 2.8x"
  dir?: 'up' | 'down';
  tone: 'bad' | 'neutral' | 'good';
}
export interface CardFace {
  // existing v0 shape (unchanged, still written by the collector)
  primary: CardFaceStat & { tone: 'bad' | 'neutral' | 'good' };
  support: CardFaceStat[];
  childCount?: number;
  // v1.1 slot grammar (additive)
  problemLabel?: string;         // Slot-0 words: "Below target" | "Leak: Clicks to Sessions" | ...
  delta?: CardFaceDelta;         // Slot A
  denominator?: { value: string; label: string };  // Slot B
  impactPerMonth?: number | null;                  // Slot C numeric; equals item.magnitude (monthly USD)
  impactLabel?: 'gap' | 'headroom' | 'at risk';    // Slot C qualifier
}

export type CoverageOutcome = 'clean' | 'flagged' | 'insufficient_data' | 'not_applicable' | 'skipped';

// Mirror of sbly-dash-collector src/intelligence/googleWatchdog/coverage.ts.
//
// The collector owns this vocabulary outright. Names, guardrails, checklist sizes and deck
// links all travel in the payload, so the dashboard keeps no second copy to drift from what
// the checks are actually called. The five names this dashboard used to hard-code existed
// nowhere but here, which is why nobody could look one up (L, 2026-08-03).
export type CheckLayer = 'detection' | 'diagnosis';

export interface WatchdogCheckDefinition {
  check: string;
  layer: CheckLayer;
  /** What this check looks for, in the reader's words. */
  watchesFor: string;
  /** The single rule that keeps this check honest. Specialists only. */
  guardrail?: string;
  /** What it refuses to assert. Specialists only. */
  wontClaim?: string;
  /** e.g. "4 steps, 3 Critical, 1 Supporting." Specialists only. */
  checklistSummary?: string;
  /** Slide id in the explainer deck. */
  deckSlide?: string;
  evaluator: string;
}

// A definition plus what the latest run reported for this campaign.
export interface WatchdogCheck extends WatchdogCheckDefinition {
  outcome: CoverageOutcome | 'not_checked';
  detail: string | null;
}

// Mirror of sbly-dash-collector src/intelligence/googleWatchdog/coverage.ts.
export interface WatchdogCoverage { runDate: string | null; checks: WatchdogCheck[] }

// The campaign's real 7-day funnel chain for the detail math block. Optional:
// absent on pre-v1.1 cards and non-campaign digests. sessions7d/atc7d may arrive
// as 0 when the collector's source columns are absent, render '-' for zero-valued
// intermediate stages, never imply a measured zero.
export interface FunnelChain {
  spend7d: number;
  clicks7d: number;
  sessions7d: number;
  atc7d: number;
  orders7d: number;
  revenue7d: number;
  aov: number | null;
}

// ── Decomposition ────────────────────────────────────────────────────────────
// Mirrors the collector's src/intelligence/decomposition/types.ts. The two must
// move together.
//
// Locked numbers only: no status, no material flag, no direction, no verdict
// sentence. The AI brief names the cause; this is the evidence it reads.
// Collector ADR 0006, dashboard ADR 0006.

export type LeverKey = 'aov' | 'order_atc_rate' | 'atc_rate' | 'c2s' | 'cpc';

/** A measured value in each of the two comparison windows. */
export interface WindowPair {
  current: number | null;
  baseline: number | null;
}

/**
 * One window of the comparison, with the reasoning that selected it — which bar a
 * healthy block had to clear, how many candidates were passed over, why it fell
 * back. A window that states only its dates hides its judgment; that is the thing
 * the ADR objects to.
 */
export interface DecompositionWindow {
  from: string;
  to: string;
  kind: 'healthy' | 'prior_period' | 'current';
  bar: 'target' | 'break_even' | null;
  rejectedBlocks: number;
  fellBackBecause: string | null;
  /** Measured promo depth, store-daily. Displayed as context; nothing branches on it. */
  avgOrderDiscount: number | null;
  discountedOrderShare: number | null;
  /** Catches BOGO weeks, which move units rather than discount amount. */
  unitsPerOrder: number | null;
}

export interface DecompositionLever {
  key: LeverKey;
  label: string;
  unit: 'usd' | 'rate' | 'ratio';
  current: number | null;
  baseline: number | null;
  /** Relative change, current vs baseline. -0.19 = down 19%. */
  deltaPct: number | null;
  /** Signed, in ln(ROAS) units. The five sum to ln(current/baseline). */
  lnContribution: number | null;
  /** Signed, in return units. What the waterfall bar draws — never a percent of a gap. */
  roasContribution: number | null;
  measured: boolean;
  unmeasuredReason: string | null;
}

export interface AtcSplit {
  rateEffectPp: number;
  mixEffectPp: number;
  coverage: number;
}

export interface AuctionPosition {
  searchIs: WindowPair;
  topIs: WindowPair;
  absTopIs: WindowPair;
  cohort: string;
}

export interface CardDecomposition {
  /** Which chart line this reconciles to. The chart draws one TW line and two Google lines. */
  revenueBasis: 'triple_whale' | 'google';
  roasCurrent: number | null;
  roasBaseline: number | null;
  /** Not "gapPct" — a gap invites dividing a lever by it, which is the banned rendering. */
  roasDeltaPct: number | null;
  currentWindow: DecompositionWindow;
  baselineWindow: DecompositionWindow;
  /** False when a lever dropped out, so the bars no longer reconcile to the move. Say so. */
  contributionsComplete: boolean;
  levers: DecompositionLever[];
  atcSplit: AtcSplit | null;
  auctionPosition: AuctionPosition | null;
}

// One row of the My campaigns picker (GET google-watchdog/groups/campaigns). `live` = enabled with
// spend in the last LIVE_CAMPAIGN_DAYS; the rest are kept because they have an open card or sit in
// a saved group. `status` is Google's own campaign status (ENABLED, PAUSED, REMOVED).
export interface CampaignOption { campaignId: string; campaignName: string; store: string | null; status: string; live: boolean }
export interface UserCampaignGroup {
  group_id: string;   // REQUIRED for the reconciled CRUD contract (idempotent save + delete-by-id)
  owner: string;
  group_name: string;
  campaign_ids: string[];
  updated_at: string;
}

export interface CardChild {
  label: string;
  type: string;
  headline: string;
  magnitude: number | null;
  /** Prefix for the money on this object; absent on cards stored before 2026-09-09, fall back to the store's. */
  currency?: string;
  findings: CardFindingLine[];
  brief?: CardBrief;
  guardrailHeader?: string;
}

export interface CardReturned {
  episode: number;
  dismissedAt: string;          // YYYY-MM-DD
  dismissedBy: string;          // 'watchdog' means the card aged out
  reason: string | null;
  trigger: 'checkup' | 'escalation';
  roas30Then: number | null;
  roas30Now: number | null;
  breakevenRoas: number | null;
  lossSinceUsd: number | null;
  lossWindowDays: number;
  dismissedDays: number;
}

export interface CardPayload {
  version: 1;
  bundleType: string;
  guardrailHeader?: string;
  /** Prefix for the money on this object; absent on cards stored before 2026-09-09, fall back to the store's. */
  currency?: string;
  findings: CardFindingLine[];
  issues?: CardIssue[];
  economics?: EconomicsFrame;
  roasStack?: CardRoasStack;
  band?: CardBand;
  divergence?: CardDivergence;
  series?: CardSeries;
  basisNote?: string;
  contextLine?: string;
  detailLines?: string[];
  brief?: CardBrief;
  briefHistory?: CardBrief[];
  children?: CardChild[];
  face?: CardFace;
  funnelChain?: FunnelChain;
  decomposition?: CardDecomposition;
  minor?: boolean;   // collector tags Minor-drawer items (F5 filters on this; mirror of collector feed.ts)
  lastDetected?: string;
  returned?: CardReturned;
  // Optional because cards that stopped being detected retain their older payload unchanged.
  costOfDelay?: CardCostOfDelay | null;
  problem?: CardProblem;
  recovery?: CardRecovery;
}

export interface StillDetectedItem {
  item_id: string;
  headline: string | null;
  campaign_name: string | null;
  store: string | null;
  resolved_by: string | null;
  dismiss_reason: string | null;
  resolved_on: string;          // YYYY-MM-DD
  monthly_usd: number | null;
  /** Prefix for the money on this object; absent on cards stored before 2026-09-09, fall back to the store's. */
  currency?: string | null;
  checkup_on: string;           // YYYY-MM-DD, the latest it returns
}

export interface WatchdogHealth {
  run_date: string;
  mode: string;
  started_at: string;
  finished_at: string | null;
  active_stores: string[] | null;
  skipped_stores: { store: string; reason: string | null }[] | null;
  items_created: number | null;
  items_updated: number | null;
  items_auto_dismissed: number | null;
  error: string | null;
  hours_since_last_success: number | null;
  last_success_at?: string | null;
  failed_evaluators?: string[] | string | null;   // ARRAY_AGG: array, JSON string, or NULL (= no failures)
  still_detected?: StillDetectedItem[] | null; // null means the lookup failed, [] means nothing is still detected
}

// One Google ad's totals over a date range, from GET /google-watchdog/performance. Mirror of
// sbly-dash-collector src/sbly-api-server/google-watchdog/performanceQueries.js. Money is in the
// store's own currency; revenue excludes sales tax (new-customer revenue does not).
export interface PerformanceRow {
  store: string;
  campaign_id: string;
  campaign_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  ad_id: string | null;
  ad_name: string | null;
  spend: number;
  impressions: number;
  clicks: number;
  sessions: number;
  atc: number;
  orders: number;
  revenue: number;
  nc_orders: number;
  nc_revenue: number;
  google_value: number;
  google_conversions: number;
}

// Mirror of sbly-dash-collector src/intelligence/googleWatchdog/actionSteps.ts: a change an agent
// proposes on a card, which a person approves or rejects on the card (triage view only).
export type ActionStepStatus = 'waiting' | 'approved' | 'rejected' | 'applied' | 'validated' | 'failed' | 'withdrawn' | 'done';
export const ACTION_STEP_STORES = ['NOONLASH'];
// Steps made by someone (the agent) after approval rather than in Google Ads at once.
export const PERSON_STEP_OPS = ['site_change', 'other'];
// A whole new responsive search ad, tested beside the ads running in its ad group (never an edit to one).
export const TEST_AD_OP = 'create_test_ad';
// A fix to a running ad's own lines (an outdated year, a typo, a claim no longer true), shown before and after.
export const FIX_AD_OP = 'fix_ad_text';

export interface ActionStep {
  stepId: string;
  store: string;
  itemId: string | null;
  op: string;
  params: Record<string, unknown>;
  title: string;
  why: { summary: string; points: string[] };
  expected: string | null;
  proposedBy: string;
  proposedAt: string;
  status: ActionStepStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  note: string | null;
  result: {
    link?: string;
    before?: unknown;
    verified?: boolean;
    // A test ad, once made: its id and Google's first review of it.
    adId?: string | null;
    approval?: string | null;
    adStrength?: string | null;
  } | null;
  history: { event: string; actor: string; at: string; note: string | null }[];
}
